import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Save, Landmark, Loader2, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (n) => `€ ${Number(n).toFixed(2)}`;
const fmt = (d) => { try { return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

export const AdminDonations = ({ token }) => {
  const hdr = { "Content-Type": "application/json", "X-Admin-Password": token };
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/donations`, { headers: { "X-Admin-Password": token } });
      setItems(await r.json());
    } catch { /* noop */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetch(`${API}/api/admin/donation-settings`, { headers: hdr }).then((r) => r.json()).then(setS).catch(() => {}); // eslint-disable-line
    load();
  }, [load]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    const r = await fetch(`${API}/api/admin/donation-settings`, { method: "PUT", headers: hdr, body: JSON.stringify(s) });
    setSaving(false);
    if (r.ok) toast.success("Impostazioni donazioni salvate."); else toast.error("Errore salvataggio.");
  };
  const f = (k, ph) => <input data-testid={`donation-${k}`} value={(s && s[k]) || ""} onChange={(e) => setS((v) => ({ ...v, [k]: e.target.value }))} placeholder={ph} className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#2E4F3B]" />;

  const totalCompleted = items.filter((d) => d.status === "completata").reduce((a, d) => a + d.amount, 0);

  return (
    <div data-testid="admin-donations" className="rounded-3xl border-2 border-[#E5E1D8] bg-white p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Heart className="w-5 h-5 text-[#C15C3D]" fill="currentColor" /><h3 className="font-display text-xl font-black text-[#1A1A1A]">Donazioni</h3></div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-[#FBF9F6]" title="Aggiorna"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {s && (
        <div className="rounded-2xl border-2 border-[#E5E1D8] bg-[#FBF9F6] p-4 mb-5">
          <div className="flex items-center gap-2 mb-3"><Landmark className="w-4 h-4 text-[#2E4F3B]" /><h4 className="font-bold text-[#1A1A1A]">Dati bonifico (mostrati se PayPal non è attivo)</h4></div>
          <div className="grid sm:grid-cols-2 gap-2">
            {f("iban", "IBAN")}
            {f("holder", "Intestatario")}
            {f("causale", "Causale")}
            {f("bank_note", "Nota (facoltativa)")}
          </div>
          <button data-testid="donation-save" onClick={save} disabled={saving} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#2E4F3B] text-white font-bold px-5 py-2.5 text-sm disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-[#5C5C5C]">{items.length} donazioni</span>
        <span className="font-display font-black text-[#2E4F3B]">Totale incassato: {eur(totalCompleted)}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-[#5C5C5C]"><Loader2 className="w-5 h-5 animate-spin mr-2" />Caricamento…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#5C5C5C] italic text-center py-8">Nessuna donazione ricevuta.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F6] border-b border-[#E5E1D8] text-left">
              <tr><th className="px-3 py-2">Data</th><th className="px-3 py-2">Da</th><th className="px-3 py-2">Importo</th><th className="px-3 py-2">Stato</th></tr>
            </thead>
            <tbody className="divide-y divide-[#E5E1D8]">
              {items.map((d) => (
                <tr key={d.id}>
                  <td className="px-3 py-2 whitespace-nowrap text-[#5C5C5C]">{fmt(d.created_at)}</td>
                  <td className="px-3 py-2">{d.name || "Anonimo"}{d.email ? <div className="text-[11px] text-[#9a9a9a]">{d.email}</div> : null}</td>
                  <td className="px-3 py-2 font-bold whitespace-nowrap">{eur(d.amount)}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-[11px] font-bold ${d.status === "completata" ? "bg-emerald-100 text-emerald-800" : d.status === "in_attesa" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>{d.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
